import { EntityId } from '../../../../../../lib/charting_library/charting_library';
import TradingView from '../../../../../trading-view';
import { TpSlDragIntent } from './tp-sl-drag-intent';
import { tpSlZoneFillColor } from './tp-sl-zone-fill';

/**
 * 드래그 중 진입가↔드래그가 사이를 채우는 손익존(반투명 사각형)을 그린다.
 *
 * Flow A(신규 생성)와 Flow B(기존 TP/SL 수정 드래그)가 공통으로 쓴다. 호출부는 반환된
 * 핸들을 보관했다가 다음 프레임에 `existing`으로 다시 넘기면 좌표만 갱신(같은 kind)하거나
 * kind가 바뀌면(진입가를 넘나듦) 색을 바꾸기 위해 다시 그린다.
 */

export interface TpSlAreaHandle {
  shapeId: EntityId;
  kind: TpSlDragIntent['kind'];
}

export function drawTpSlArea(
  tv: TradingView,
  fromPrice: number,
  toPrice: number,
  kind: TpSlDragIntent['kind'],
  existing: TpSlAreaHandle | undefined,
): TpSlAreaHandle | undefined {
  const chart = tv.activeChart;
  if (!chart) return existing;

  // onMoving마다(매 프레임) 호출된다. teardown 중이거나 차트 내부 상태가 일시적으로 비정상일 때
  // charting_library가 throw할 수 있으므로 전체를 감싸고, 실패 시 드래그 핸들러가 깨지지 않게
  // 기존 핸들을 그대로 반환한다(다른 헬퍼의 "teardown 중 null 에러" 방어 패턴과 통일).
  try {
    const range = chart.getVisibleRange();
    const points = [
      { time: range.from, price: fromPrice },
      { time: range.to, price: toPrice },
    ];

    if (existing) {
      // 같은 kind면 좌표만 갱신. kind가 바뀌면(진입가를 넘나듦) 색 적용을 위해 다시 그린다.
      if (existing.kind === kind) {
        try {
          chart.getShapeById(existing.shapeId).setPoints(points);
          return existing;
        } catch {
          // shape이 사라졌으면 아래에서 재생성
        }
      } else {
        removeTpSlArea(tv, existing);
      }
    }

    const shapeId = chart.createMultipointShape(points, {
      shape: 'rectangle',
      lock: true,
      disableSelection: true,
      disableSave: true,
      // createMultipointShape는 (applyOverrides가 쓰는 `linetoolrectangle.` 접두사가 아닌)
      // 맨 키를 쓴다. 레포의 triangle-fill 패턴을 따른다.
      overrides: {
        color: 'rgba(0, 0, 0, 0)', // 투명 테두리
        backgroundColor: tpSlZoneFillColor(kind), // TP=수익존 / SL=손실존
        fillBackground: true,
        linewidth: 0,
        showLabel: false,
      },
    });

    return shapeId != null ? { shapeId, kind } : undefined;
  } catch {
    // teardown 중/차트 비정상 — 기존 핸들 유지(다음 프레임에 자기복구)
    return existing;
  }
}

export function removeTpSlArea(tv: TradingView, handle: TpSlAreaHandle | undefined): void {
  if (!handle) return;
  try {
    tv.activeChart?.removeEntity(handle.shapeId);
  } catch {
    // already removed
  }
}

/** 현재 보이는 가격범위 기준 픽셀당 가격값. 라벨을 라인 위로 N픽셀 띄울 때 쓴다. 실패 시 0. */
export function pricePerPixel(tv: TradingView): number {
  try {
    const pane = tv.activeChart?.getPanes()[0];
    const range = pane?.getMainSourcePriceScale()?.getVisiblePriceRange();
    const height = pane?.getHeight();
    if (!range || !height) return 0;
    return (range.to - range.from) / height;
  } catch {
    return 0;
  }
}
